/**
 * Den Labelmaker direkt aus dem Browser ansprechen -- WebUSB.
 *
 * Geht in Chrome und Edge, auf Android mit einem USB-OTG-Kabel. Safari und
 * Firefox koennen es nicht. Auf Windows belegt der Brother-Treiber das Geraet,
 * sobald er installiert ist; dann bleibt der Weg ueber das PDF und den
 * Druckdialog (siehe Etiketten-Seite).
 *
 * Kennung laut Handbuch (Anhang A): Hersteller 0x04F9, PT-E550W 0x2060,
 * PT-P750W 0x2062, PT-P710BT 0x20AF -- alle mit demselben Rasterprotokoll.
 */

import { STATUS_ANFRAGE, statusLesen, type DruckerStatus } from "./ptouch.js";

// --- Das Wenige an WebUSB, das hier gebraucht wird -------------------------------

interface UsbEndpunkt {
  endpointNumber: number;
  direction: "in" | "out";
  type: "bulk" | "interrupt" | "isochronous";
}
interface UsbGeraet {
  productName?: string;
  opened: boolean;
  configuration: {
    interfaces: { interfaceNumber: number; alternate: { endpoints: UsbEndpunkt[] } }[];
  } | null;
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(n: number): Promise<void>;
  claimInterface(n: number): Promise<void>;
  transferOut(ep: number, daten: BufferSource): Promise<{ status: string }>;
  transferIn(ep: number, laenge: number): Promise<{ status: string; data?: DataView }>;
}
interface Usb {
  requestDevice(o: { filters: { vendorId: number }[] }): Promise<UsbGeraet>;
  getDevices(): Promise<UsbGeraet[]>;
}

const usb = (): Usb | undefined => (globalThis.navigator as { usb?: Usb }).usb;

/** Kann dieser Browser ueberhaupt USB? */
export function webUsbVerfuegbar(): boolean {
  return usb() !== undefined && globalThis.isSecureContext;
}

const BROTHER = 0x04f9;

export class DruckFehler extends Error {}

/** Eine offene Verbindung zum Labelmaker. */
export interface Labelmaker {
  name: string;
  status(): Promise<DruckerStatus>;
  /** Schickt den Auftrag und wartet, bis alle `seiten` Etiketten gedruckt sind. */
  drucken(auftrag: Uint8Array<ArrayBuffer>, seiten: number): Promise<DruckerStatus>;
  schliessen(): Promise<void>;
}

/** Wartet hoechstens `ms` -- ein Drucker, der nicht antwortet, soll die Seite nicht einfrieren. */
function mitFrist<T>(zusage: Promise<T>, ms: number, text: string): Promise<T> {
  return Promise.race([
    zusage,
    new Promise<T>((_, scheitern) =>
      setTimeout(() => {
        scheitern(new DruckFehler(text));
      }, ms),
    ),
  ]);
}

/**
 * Verbindet mit dem Labelmaker.
 *
 * Beim ersten Mal fragt der Browser, welches Geraet -- danach kennt er es.
 * `waehlen` muss aus einem Tipp heraus kommen; der Browser erlaubt die
 * Auswahl sonst nicht.
 */
export async function labelmakerVerbinden(waehlen: boolean): Promise<Labelmaker> {
  const u = usb();
  if (u === undefined) {
    throw new DruckFehler(
      "Dieser Browser kann nicht direkt über USB drucken. Chrome oder Edge nehmen.",
    );
  }

  const bekannt = (await u.getDevices())[0];
  const geraet =
    bekannt ?? (waehlen ? await u.requestDevice({ filters: [{ vendorId: BROTHER }] }) : undefined);
  if (geraet === undefined) throw new DruckFehler("Kein Labelmaker verbunden");

  try {
    if (!geraet.opened) await geraet.open();
    if (geraet.configuration === null) await geraet.selectConfiguration(1);
    await geraet.claimInterface(0);
  } catch (f) {
    throw new DruckFehler(
      "Der Labelmaker lässt sich nicht öffnen. Auf einem PC mit Brother-Treiber belegt der " +
        "Treiber das Gerät — dann über „Als PDF“ drucken. " +
        `(${f instanceof Error ? f.message : String(f)})`,
    );
  }

  const endpunkte = geraet.configuration?.interfaces[0]?.alternate.endpoints ?? [];
  const aus = endpunkte.find((e) => e.direction === "out" && e.type === "bulk")?.endpointNumber;
  const ein = endpunkte.find((e) => e.direction === "in" && e.type === "bulk")?.endpointNumber;
  if (aus === undefined || ein === undefined) {
    throw new DruckFehler("Das Gerät meldet sich nicht wie ein Brother-Labelmaker");
  }

  /** Liest die naechste Statusmeldung (32 Byte). Leere Antworten ueberspringen. */
  const lesen = async (fristMs: number): Promise<DruckerStatus> => {
    const ende = Date.now() + fristMs;
    while (Date.now() < ende) {
      const antwort = await mitFrist(
        geraet.transferIn(ein, 32),
        Math.max(500, ende - Date.now()),
        "Der Labelmaker antwortet nicht. Eingeschaltet und das Kabel richtig drin?",
      );
      const daten = antwort.data;
      if (daten !== undefined && daten.byteLength >= 32) {
        return statusLesen(new Uint8Array(daten.buffer, daten.byteOffset, 32));
      }
      await new Promise((fertig) => setTimeout(fertig, 100));
    }
    throw new DruckFehler("Der Labelmaker antwortet nicht");
  };

  return {
    name: geraet.productName ?? "Brother-Labelmaker",

    async status() {
      await geraet.transferOut(aus, STATUS_ANFRAGE);
      return lesen(3000);
    },

    /**
     * Schickt den Auftrag und wartet, bis der Drucker „fertig" oder einen
     * Fehler meldet. Waehrend des Drucks darf man ihn nichts fragen
     * (Handbuch 1 (3)) -- er meldet sich von selbst.
     */
    async drucken(auftrag, seiten) {
      await geraet.transferOut(aus, auftrag);
      // Je Etikett eine Minute -- ein langes Band bei 30 mm/s braucht Zeit.
      const ende = Date.now() + 60_000 * Math.max(1, seiten);
      let fertig = 0;
      while (Date.now() < ende) {
        const s = await lesen(ende - Date.now());
        if (s.art === "fehler") {
          throw new DruckFehler(s.fehler.join(", ") || "Der Labelmaker meldet einen Fehler");
        }
        if (s.art === "fertig") {
          fertig += 1;
          if (fertig >= seiten) return s;
        }
      }
      throw new DruckFehler("Der Druck hat zu lange gedauert");
    },

    async schliessen() {
      await geraet.close();
    },
  };
}
